import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex, Text } from "styles/utility-components";

import Modal from "components/ui/modal/Modal";
import { AiToneOfVoice } from "lib/hooks/api/ai_tone_of_voice/useAiToneOfVoices";
import { FileWithDirectoryPath } from "lib/hooks/api/customer_documents/types";
import ToneOfVoiceModalContent from "./ToneOfVoiceModalContent";
import {
  ToneOfVoiceProcessingState,
  useToneOfVoiceDocumentProcessing,
} from "./useToneOfVoiceDocumentProcessing";
import { ToneOfVoiceFormValues, toneOfVoiceSchema, ViewState } from "./utils";

function getViewState(isManual: boolean, status: ToneOfVoiceProcessingState["status"]): ViewState {
  if (isManual) return "manual";
  if (status === "streaming" || status === "success") return "review";
  if (status === "uploading" || status === "processing") return "processing";
  return "upload";
}

function getTitle(mode: "create" | "edit", viewState: ViewState): string {
  if (mode === "edit") {
    return "Edit tone of voice";
  }
  if (viewState === "review") {
    return "Review tone of voice";
  }
  return "Create new Tone of voice";
}

type Props = {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  entry?: AiToneOfVoice;
  existingTovCount?: number;
};

export function ToneOfVoiceFormModal({
  open,
  onClose,
  mode,
  entry,
  existingTovCount = 0,
}: Props): JSX.Element {
  const [isManual, setIsManual] = useState<boolean>(mode === "edit");
  const [selectedFiles, setSelectedFiles] = useState<FileWithDirectoryPath[]>([]);

  const methods = useForm<ToneOfVoiceFormValues>({
    defaultValues: {
      label: entry?.label ?? "",
      rawValue: entry?.rawValue ?? "",
      isDefault: entry?.isDefault ?? false,
    },
    resolver: zodResolver(toneOfVoiceSchema),
    mode: "onChange",
  });

  const { reset, setValue } = methods;

  const handleProcessingSuccess = useCallback(
    (result: { rawValue: string; label: string }) => {
      setValue("rawValue", result.rawValue, { shouldValidate: true });
      setValue("label", result.label, { shouldValidate: true });
    },
    [setValue],
  );

  const {
    state: processingState,
    startUpload,
    reset: resetProcessing,
  } = useToneOfVoiceDocumentProcessing({ onSuccess: handleProcessingSuccess });

  const closeModal = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  const viewState = getViewState(isManual, processingState.status);

  // Reset form and processing state when the modal opens or mode changes.
  useEffect(() => {
    if (open) {
      reset({
        label: entry?.label ?? "",
        rawValue: entry?.rawValue ?? "",
        isDefault: entry?.isDefault ?? false,
      });
      setIsManual(mode === "edit");
      setSelectedFiles([]);
      resetProcessing();
    }
  }, [open, entry, reset, mode, resetProcessing]);

  return (
    <Modal
      open={open}
      // Disable closing the modal while processing to prevent interrupting the
      // upload/processing flow.
      onOpenChange={viewState === "processing" ? () => {} : onClose}
      showCloseButton={false}
      maxWidth="calc(100vw - 120px)"
    >
      <FormProvider {...methods}>
        <Container column gap={16}>
          <Text h2>{getTitle(mode, viewState)}</Text>
          <ToneOfVoiceModalContent
            viewState={viewState}
            processingState={processingState}
            existingTovCount={existingTovCount}
            mode={mode}
            closeModal={closeModal}
            setManualMode={setIsManual}
            selectedFiles={selectedFiles}
            startUpload={startUpload}
            entry={entry}
            setSelectedFiles={setSelectedFiles}
            resetProcessing={resetProcessing}
          />
        </Container>
      </FormProvider>
    </Modal>
  );
}

const Container = styled(Flex)({
  padding: 16,
  // Cap to the dialog's inner available height (dialog maxHeight is
  // calc(100vh - 26px), minus 32px DialogWrapper padding) and scroll
  // internally so CTAs stay reachable when AI content is long.
  maxHeight: "calc(100vh - 58px)",
  width: "60vw",
  minWidth: 780,
  overflowY: "auto",
});
